import type { LucideIcon } from "lucide-react";
import {
  AgentsIcon,
  AlarmClockIcon,
  LayoutGridIcon,
  MessagesSquareIcon,
  ShieldCheckIcon,
  WrenchIcon,
  ZapIcon,
} from "lucide-react";

import type { MessageKey } from "~/i18n";
import type { ActivityEvent, ActivityKind, ActivityStatus } from "./activitiesTypes";

const FILTER_ICON_CLASS = "size-[15px] shrink-0";

export function activityKindIcon(kind: ActivityKind): LucideIcon {
  switch (kind) {
    case "agent_run":
      return AgentsIcon;
    case "workflow_run":
      return ZapIcon;
    case "allowance":
      return ShieldCheckIcon;
    case "schedule":
      return AlarmClockIcon;
    case "comms":
      return MessagesSquareIcon;
    case "board":
      return LayoutGridIcon;
    case "tool_action":
    default:
      return WrenchIcon;
  }
}

export { FILTER_ICON_CLASS };

export function activityStatusLabelKey(status: ActivityStatus): MessageKey {
  switch (status) {
    case "needs_approval":
      return "activities.status.needsApproval";
    case "pending":
      return "activities.status.pending";
    case "running":
      return "activities.status.running";
    case "completed":
      return "activities.status.completed";
    case "failed":
      return "activities.status.failed";
    case "approved":
      return "activities.status.approved";
    case "denied":
      return "activities.status.denied";
    default:
      return "activities.status.pending";
  }
}

export function activityKindLabelKey(kind: ActivityKind): MessageKey {
  switch (kind) {
    case "tool_action":
      return "activities.kind.tool";
    case "allowance":
      return "activities.kind.allowance";
    case "workflow_run":
      return "activities.kind.workflow";
    case "agent_run":
      return "activities.kind.agent";
    case "comms":
      return "activities.kind.comms";
    case "schedule":
      return "activities.kind.schedule";
    case "board":
      return "activities.kind.board";
    default:
      return "activities.kind.tool";
  }
}

export function activityLaneTitleKey(lane: "todo" | "doing" | "done"): MessageKey {
  switch (lane) {
    case "todo":
      return "activities.lane.todo";
    case "doing":
      return "activities.lane.doing";
    case "done":
      return "activities.lane.done";
  }
}

export function toolkitLogoUrl(toolkit: string | undefined): string | null {
  if (!toolkit?.trim()) return null;
  const slug = toolkit.trim().toLowerCase();
  return `https://logos.composio.dev/api/${slug}`;
}

export function formatActivityTime(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

export function formatDayChipLabel(dayKey: string, locale: string): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!y || !m || !d) return dayKey;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
}

export function agentDisplayLabel(event: ActivityEvent): string | null {
  return event.refs.agentLabel ?? null;
}

export type ActivityProgressTone = "muted" | "primary" | "warning" | "success" | "destructive";

/** The ONE source of truth: a status → colour tone. Everything coloured by status (the card
 * badge, the timeline dot, the lifecycle bead) reads from here, so a colour always means a real
 * state — never a fabricated one. */
export function statusTone(status: ActivityStatus): ActivityProgressTone {
  switch (status) {
    case "running":
      return "primary";
    case "needs_approval":
      return "warning";
    case "completed":
    case "approved":
      return "success";
    case "failed":
    case "denied":
      return "destructive";
    default:
      return "muted"; // pending
  }
}

/** Colour classes for the status pill shown on a lane card — so `failed` never looks like
 * `completed` at a glance (the card is the surface people actually scan). */
export function activityStatusBadgeClass(status: ActivityStatus): string {
  switch (statusTone(status)) {
    case "primary":
      return "border-primary/30 bg-primary/10 text-primary";
    case "warning":
      return "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400";
    case "success":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "destructive":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    default:
      return "border-border bg-muted text-muted-foreground";
  }
}

export type ActivityStepState = "done" | "current" | "upcoming" | "error";

export type ActivityLifecycleStep = {
  readonly labelKey: MessageKey;
  readonly state: ActivityStepState;
  readonly detail?: string;
  readonly at?: string;
};

/** The single bead's state for an event with no per-step journey — the REAL outcome, nothing more. */
function statusToStepState(status: ActivityStatus): ActivityStepState {
  switch (status) {
    case "completed":
    case "approved":
      return "done";
    case "failed":
    case "denied":
      return "error";
    case "running":
    case "needs_approval":
    case "pending":
      return "current";
    default:
      return "upcoming";
  }
}

/** The stage labels for a kind — only used to name the beads of an event that has a REAL
 * multi-step journey. Events without a journey never borrow these (they'd be fiction). */
function kindStepLabels(kind: ActivityKind): readonly MessageKey[] {
  switch (kind) {
    case "allowance":
      return ["activities.step.requested", "activities.step.review", "activities.step.decision"];
    case "workflow_run":
      return [
        "activities.step.triggered",
        "activities.step.inProgress",
        "activities.step.finished",
      ];
    case "agent_run":
    case "board":
      return ["activities.step.started", "activities.step.inProgress", "activities.step.finished"];
    case "schedule":
      return ["activities.step.scheduled", "activities.step.due", "activities.step.ran"];
    case "comms":
      return [
        "activities.step.received",
        "activities.step.processing",
        "activities.step.delivered",
      ];
    case "tool_action":
    default:
      return ["activities.step.queued", "activities.step.executing", "activities.step.finished"];
  }
}

/**
 * Lifecycle beads for an event — every bead corresponds to something REAL:
 * - If the event carries a `journey` (real per-stage beats), render ONE bead per beat: a beat
 *   with a timestamp is `done`; the last unfinished beat takes the event's live state
 *   (current/error); the rest are `upcoming`.
 * - Otherwise (a point-in-time run-log row) render a SINGLE bead reflecting the actual outcome —
 *   no invented 3-step "progress", no fake greens before a failure.
 */
export function activityLifecycleSteps(event: ActivityEvent): ActivityLifecycleStep[] {
  const { kind, status, journey } = event;
  if (journey?.length) {
    const labels = kindStepLabels(kind);
    return journey.map((beat, i) => {
      const isLast = i === journey.length - 1;
      const state: ActivityStepState = beat.at
        ? "done"
        : isLast
          ? statusToStepState(status)
          : "upcoming";
      return {
        labelKey: labels[i] ?? activityStatusLabelKey(status),
        state,
        detail: beat.detail,
        ...(beat.at ? { at: beat.at } : {}),
      };
    });
  }
  return [{ labelKey: activityStatusLabelKey(status), state: statusToStepState(status) }];
}

export function activityTimelineDotClass(status: ActivityStatus): string {
  switch (statusTone(status)) {
    case "primary":
      return "bg-primary ring-2 ring-primary/25";
    case "warning":
      return "bg-amber-500 ring-2 ring-amber-500/25";
    case "success":
      return "bg-emerald-500";
    case "destructive":
      return "bg-destructive";
    default:
      return "bg-muted-foreground/50";
  }
}

/** Absolute date + time for the detail sheet (local TZ, locale-aware) — the one place we show the
 * full timestamp, so a viewer can see the real date, not just HH:MM. */
export function formatActivityDateTime(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
